'use client';

import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
  type ReactNode,
} from 'react';
import SpecialPriceProductSection from '@wconcept/wconcept-ui-renewal/components/organisms/_section/SpecialPriceProductSection';
import DateTabItem from '@wconcept/wconcept-ui-renewal/components/molecules/_item/DateTabItem';
import MenuBarItem from '@wconcept/wconcept-ui-renewal/components/molecules/_item/MenuBarItem';
import Menu2LineBarItem from '@wconcept/wconcept-ui-renewal/components/molecules/_item/Menu2LineBarItem';
import useRenderProducts from '@wconcept/wconcept-display/hooks/useRenderProducts';
import { useServerTimeActions } from '@store/serverTimeStore';
import {
  PromotionFirstPurchaseSchedule,
  PeriodType,
  PromotionScheduleTab,
} from '@wconcept/wconcept-common/models/renewal/promotion';
import numberToKorean from '@wconcept/wconcept-common/utils/numberToKorean';
import { type ICouponInfo } from '@wconcept/wconcept-common/models/event/custom';
import { Product } from '@wconcept/wconcept-common/models/renewal/product';
import useGa4Event from '@hooks/useGa4Event';
import dayjs from '@wconcept/wconcept-common/libs/dayjs';
import PrdItemSkeleton from '@wconcept/wconcept-ui-renewal/src/components/molecules/_skeleton/PrdItemSkeleton';
import { usePromotionTabProducts } from '@wconcept/wconcept-display/hooks/usePromotionTabProducts';
import useMolocoLogger from '@hooks/useMolocoLogger';
import useBrazeLogger from '@hooks/useBrazeLogger';
import { type PromotionModuleVisualTokens } from '@utils/promotionContentVisual';
import { usePromotionModuleCustomColors } from '@hooks/promotion/usePromotionModuleCustomColors';
import { useCouponDownload } from '@hooks/useCouponDownload';
import useGlobalState from '@hooks/useGlobalState';
import type { WckAuthCookie } from '@wconcept/wconcept-common/models/display/common';
import { keepPreviousData, useQueries } from '@tanstack/react-query';
import { requestGetPromotionProducts } from '@apis/renewal/promotion';

const FIRST_PURCHASE_MIN_ITEM_CD = 1;
const FIRST_PURCHASE_MIN_PAIR_COUNT = 2;
const FIRST_PURCHASE_DATE_TAB_PERIOD_TYPES: PeriodType[] = ['DAILY', 'PERIOD'];

const FIRST_PURCHASE_MAX_PRODUCTS_PER_TAB = 30;
const FIRST_PURCHASE_MAX_PRODUCTS_FLAT = 100;
const FIRST_PURCHASE_MAX_TAB_COUNT = 20;

const INIT_MAX_COUNT = 12;
const DEFAULT_FIRST_PURCHASE_COUPON_TITLE = '첫구매 할인 전용쿠폰';
const BRIDGE_FIRST_PURCHASE_SHOW = 'N';

const PROMOTION_TAB_PRODUCT_CHUNK_SIZE = 30;

function chunkPromotionItemCdArray<T>(array: T[], size: number): T[][] {
  const chunkList: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunkList.push(array.slice(i, i + size));
  }
  return chunkList;
}

async function fetchPromotionProductsByItemCdChunks(
  itemCdList: string[]
): Promise<Product[]> {
  if (!itemCdList.length) return [];
  const batchList = chunkPromotionItemCdArray(
    itemCdList,
    PROMOTION_TAB_PRODUCT_CHUNK_SIZE
  );
  const resultList = await Promise.all(
    batchList.map(batch =>
      requestGetPromotionProducts({
        itemCdList: batch,
        itemStatusCdList: ['02', '04'],
        productType: 'SECRET',
      })
    )
  );
  return resultList.flatMap(r => r?.productList || []);
}

function gatherFirstPurchaseScheduleItemCdList(
  schedule: PromotionFirstPurchaseSchedule
): string[] {
  const mergedList = [
    ...(schedule.itemCdList || []),
    ...(schedule.tabs?.flatMap(t => t.itemCdList || []) || []),
  ];
  return [...new Set(mergedList)];
}

function getFirstPurchaseQualifiedCategoryTabList(
  tabs: PromotionScheduleTab[] | null | undefined
): PromotionScheduleTab[] {
  if (!tabs?.length) return [];
  return tabs
    .filter(tab => (tab.itemCdList?.length ?? 0) >= FIRST_PURCHASE_MIN_ITEM_CD)
    .slice(0, FIRST_PURCHASE_MAX_TAB_COUNT);
}

function countFirstPurchaseCategoryTabsWithMappedProducts(
  qualifiedCategoryTabList: PromotionScheduleTab[],
  productList: Product[]
): number {
  if (!qualifiedCategoryTabList.length || !productList.length) {
    return 0;
  }
  const mappedItemCdSet = new Set(
    productList
      .map(productRow => productRow.itemCd)
      .filter((itemCd): itemCd is string => typeof itemCd === 'string')
  );
  return qualifiedCategoryTabList.filter(categoryTab =>
    (categoryTab.itemCdList ?? []).some(itemCd => mappedItemCdSet.has(itemCd))
  ).length;
}

/** 시크릿 `productsMap`에 매핑된 카테고리 탭만. */
function getFirstPurchaseMappedQualifiedCategoryTabList(
  qualifiedCategoryTabList: PromotionScheduleTab[],
  productMapByItemCd: Map<string, Product>
): PromotionScheduleTab[] {
  if (!qualifiedCategoryTabList.length || productMapByItemCd.size === 0) {
    return [];
  }
  return qualifiedCategoryTabList.filter(categoryTab =>
    (categoryTab.itemCdList ?? []).some(itemCd => {
      const normalized = String(itemCd ?? '').trim();
      return normalized.length > 0 && productMapByItemCd.has(normalized);
    })
  );
}

function scheduleMeetsFirstPurchaseScheduleRules(
  schedule: PromotionFirstPurchaseSchedule
): boolean {
  if (schedule.tabDisplayEnabled) {
    const qualifiedTabList = getFirstPurchaseQualifiedCategoryTabList(
      schedule.tabs
    );
    return qualifiedTabList.length >= FIRST_PURCHASE_MIN_PAIR_COUNT;
  }
  return (schedule.itemCdList?.length ?? 0) >= FIRST_PURCHASE_MIN_ITEM_CD;
}

function normalizeBridgeFirstPurchaseYn(value: string | undefined): string {
  return String(value ?? '')
    .trim()
    .toUpperCase();
}

function shouldShowFirstPurchaseModule(
  isLogin: boolean,
  user: WckAuthCookie
): boolean {
  if (!isLogin) {
    return true;
  }
  return (
    normalizeBridgeFirstPurchaseYn(user.firstPurchaseYN) ===
    BRIDGE_FIRST_PURCHASE_SHOW
  );
}

interface FirstPurchaseModuleProps {
  bgType: string;
  visualTokens: PromotionModuleVisualTokens;
  isDesktop: boolean;
  subTitle?: string;
  title?: string;
  promotionId: number;
  periodType: PeriodType;
  schedules: PromotionFirstPurchaseSchedule[];
  contentId?: string;
  promotionTitle: string;
  isVisible?: boolean;
  titleBannerPcImageUrl?: string;
  titleBannerMoImageUrl?: string;
  moduleSubText?: string;
}

function FirstPurchaseModuleContent({
  bgType,
  visualTokens,
  isDesktop,
  subTitle,
  title,
  periodType,
  schedules,
  promotionId,
  promotionTitle,
  contentId,
  isVisible,
  titleBannerPcImageUrl,
  titleBannerMoImageUrl,
  moduleSubText,
}: FirstPurchaseModuleProps) {
  const { downloadSingleCoupon } = useCouponDownload();
  const { getAdjustedTime, isCurrentDateInRange } = useServerTimeActions();

  const getCouponDescFromInfo = useCallback((info: ICouponInfo) => {
    if (info.maxDiscountAmount) {
      return `최대 ${numberToKorean(info.maxDiscountAmount, 'mixed')}원 할인`;
    }
    return '';
  }, []);

  const {
    presetBgType,
    customColorsFull,
    productUnitColorType: promotionProductUnitColorType,
    productSkeletonBgType: promotionProductSkeletonBgType,
  } = usePromotionModuleCustomColors({
    bgType,
    visualTokens,
    mask: 'full',
  });

  const sectionColorType = customColorsFull ?? presetBgType;
  const productSkeletonKeyList = useMemo(
    () =>
      Array.from(
        { length: INIT_MAX_COUNT },
        (_, index) => `first-purchase-skeleton-${index}`
      ),
    []
  );

  const validSchedules = useMemo(
    () =>
      schedules?.filter(schedule =>
        scheduleMeetsFirstPurchaseScheduleRules(schedule)
      ) ?? [],
    [schedules]
  );

  const isFirstPurchaseDateTabPeriod =
    FIRST_PURCHASE_DATE_TAB_PERIOD_TYPES.includes(periodType);

  const firstPurchaseSchedulePrefetchMetaList = useMemo(
    () =>
      validSchedules.map(schedule => ({
        scheduleId: schedule.scheduleId,
        itemCdList: gatherFirstPurchaseScheduleItemCdList(schedule),
      })),
    [validSchedules]
  );

  const firstPurchaseDateTabPrefetchEnabled =
    !!isVisible &&
    isFirstPurchaseDateTabPeriod &&
    firstPurchaseSchedulePrefetchMetaList.length >=
      FIRST_PURCHASE_MIN_PAIR_COUNT;

  const scheduleDateTabPrefetchQueryList = useQueries({
    queries: firstPurchaseSchedulePrefetchMetaList.map(meta => ({
      queryKey: [
        'wckQueryKey',
        'promotion-tab-products',
        contentId ?? '',
        String(meta.scheduleId),
        'SECRET',
      ],
      queryFn: () => fetchPromotionProductsByItemCdChunks(meta.itemCdList),
      enabled:
        firstPurchaseDateTabPrefetchEnabled && meta.itemCdList.length > 0,
      staleTime: 60 * 1000 * 5,
      gcTime: 60 * 1000 * 10,
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    })),
  });

  const isFirstPurchaseDateTabPrefetchResolved = useMemo(() => {
    if (!isFirstPurchaseDateTabPeriod) return true;
    if (firstPurchaseSchedulePrefetchMetaList.length === 0) return true;
    if (!firstPurchaseDateTabPrefetchEnabled) return true;
    if (validSchedules.length < FIRST_PURCHASE_MIN_PAIR_COUNT) {
      return true;
    }
    return firstPurchaseSchedulePrefetchMetaList.every((meta, index) => {
      const queryResult = scheduleDateTabPrefetchQueryList[index];
      if (!meta.itemCdList.length) return true;
      return !!queryResult && (queryResult.isSuccess || queryResult.isError);
    });
  }, [
    firstPurchaseDateTabPrefetchEnabled,
    firstPurchaseSchedulePrefetchMetaList,
    isFirstPurchaseDateTabPeriod,
    scheduleDateTabPrefetchQueryList,
    validSchedules.length,
  ]);

  const dateTabEligibleScheduleList = useMemo(() => {
    if (!isFirstPurchaseDateTabPeriod) return validSchedules;

    const eligibleList: PromotionFirstPurchaseSchedule[] = [];
    firstPurchaseSchedulePrefetchMetaList.forEach((meta, index) => {
      const scheduleEntry = validSchedules[index];
      if (!scheduleEntry) return;
      const productList =
        meta.itemCdList.length > 0
          ? scheduleDateTabPrefetchQueryList[index]?.data ?? []
          : [];

      const mappedProductCount = productList.length;
      if (!scheduleEntry.tabDisplayEnabled) {
        if (mappedProductCount >= 1) eligibleList.push(scheduleEntry);
        return;
      }

      const cmsQualifiedCategoryTabList =
        getFirstPurchaseQualifiedCategoryTabList(scheduleEntry.tabs);

      const categoryTabWithMappedProductCount =
        countFirstPurchaseCategoryTabsWithMappedProducts(
          cmsQualifiedCategoryTabList,
          productList
        );

      const meetsAggregateProductRule = mappedProductCount >= 1;
      const meetsCategoryProductPairRule =
        categoryTabWithMappedProductCount >= FIRST_PURCHASE_MIN_PAIR_COUNT;

      if (meetsAggregateProductRule && meetsCategoryProductPairRule) {
        eligibleList.push(scheduleEntry);
      }
    });
    return eligibleList;
  }, [
    firstPurchaseSchedulePrefetchMetaList,
    isFirstPurchaseDateTabPeriod,
    scheduleDateTabPrefetchQueryList,
    validSchedules,
  ]);

  const [selectedSchedule, setSelectedSchedule] =
    useState<PromotionFirstPurchaseSchedule | null>(
      validSchedules?.[0] ?? null
    );
  const [selectedCategoryTab, setSelectedCategoryTab] =
    useState<PromotionScheduleTab | null>(null);

  const [maxCount, setMaxCount] = useState(INIT_MAX_COUNT);

  const activeScheduleItemCodes = useMemo(() => {
    if (!selectedSchedule) return [];
    return gatherFirstPurchaseScheduleItemCdList(selectedSchedule);
  }, [selectedSchedule]);

  const {
    productsMap: tabProductsMap,
    isSuccess: isTabProductsSuccess,
    isFetching: isTabProductsFetching,
  } = usePromotionTabProducts({
    contentId: contentId || '',
    tabKey: selectedSchedule?.scheduleId?.toString() || '',
    itemCodes: activeScheduleItemCodes,
    itemStatusCd: ['02', '04'],
    productType: 'SECRET',
    enabled: isVisible && !!selectedSchedule,
  });

  const cmsQualifiedCategoryTabListForSelected = useMemo(
    () =>
      selectedSchedule?.tabDisplayEnabled
        ? getFirstPurchaseQualifiedCategoryTabList(selectedSchedule.tabs)
        : [],
    [selectedSchedule?.tabDisplayEnabled, selectedSchedule?.tabs]
  );

  const mappedQualifiedCategoryTabList = useMemo(
    () =>
      getFirstPurchaseMappedQualifiedCategoryTabList(
        cmsQualifiedCategoryTabListForSelected,
        tabProductsMap
      ),
    [cmsQualifiedCategoryTabListForSelected, tabProductsMap]
  );

  const { sendMolocoAddToWish } = useMolocoLogger();
  const { sendBrazeRenewalMyHeart } = useBrazeLogger();
  const {
    sendGa4AddToWishProduct,
    sendGa4TabClick,
    sendGa4SelectItem,
    sendGa4ButtonClick,
    sendGa4CouponClick,
  } = useGa4Event();

  const getFinalPageNo = useCallback((): number | undefined => {
    const dateTabMenuVisible =
      isFirstPurchaseDateTabPeriod &&
      (dateTabEligibleScheduleList?.length ?? 0) > 0;
    const schedulePool = dateTabMenuVisible
      ? dateTabEligibleScheduleList
      : validSchedules;
    const pageIndex = schedulePool?.findIndex(
      scheduleRow => scheduleRow.scheduleId === selectedSchedule?.scheduleId
    );
    if (pageIndex === undefined || pageIndex === -1) {
      return undefined;
    }
    return pageIndex + 1;
  }, [
    dateTabEligibleScheduleList,
    isFirstPurchaseDateTabPeriod,
    selectedSchedule?.scheduleId,
    validSchedules,
  ]);

  const getDisplayDate = (date: string) => {
    const today = dayjs().format('YYYY-MM-DD');
    if (date === today) {
      return `오늘${dayjs(date).format('MM.DD')}`;
    }
    return `${dayjs(date).format('ddd')} ${dayjs(date).format('MM.DD')}`;
  };

  const { renderProducts } = useRenderProducts({
    doAfterAddMyHeart: (product, index) => {
      sendGa4AddToWishProduct({
        product,
        myheartYn: true,
        listNo: (index ?? 0) + 1,
        ecommercePath: '프로모션',
        area: 'promotion_firstpurchase_myheart',
        issueCd: promotionId.toString(),
        issueNm: promotionTitle,
        tabNm: selectedCategoryTab?.tabName,
        pageNo: getFinalPageNo(),
        variable: title,
      });
      sendMolocoAddToWish({
        itemCd: product.itemCd,
        amount: product.finalPrice,
        pageId: window.location.pathname,
      });
      sendBrazeRenewalMyHeart({
        product,
        pageId: window.location.pathname,
        useGender: true,
        defaultGender: 'women',
      });
    },
    doAfterDeleteMyHeart: (product, index) => {
      sendGa4AddToWishProduct({
        product,
        myheartYn: false,
        listNo: (index ?? 0) + 1,
        ecommercePath: '프로모션',
        area: 'promotion_firstpurchase_myheart',
        issueCd: promotionId.toString(),
        issueNm: promotionTitle,
        tabNm: selectedCategoryTab?.tabName,
        pageNo: getFinalPageNo(),
        variable: title,
      });
    },
  });

  const selectedScheduleRef = useRef(selectedSchedule);
  selectedScheduleRef.current = selectedSchedule;

  const selectedScheduleIdRef = useRef(selectedSchedule?.scheduleId);
  selectedScheduleIdRef.current = selectedSchedule?.scheduleId;

  const selectedCategoryTabRef = useRef(selectedCategoryTab);
  selectedCategoryTabRef.current = selectedCategoryTab;

  const skipAutoSnapToTodayForUserDateTabRef = useRef(false);

  useEffect(() => {
    skipAutoSnapToTodayForUserDateTabRef.current = false;
  }, [promotionId, periodType, contentId]);

  useLayoutEffect(() => {
    if (validSchedules.length === 0) return;
    const currentScheduleId = selectedScheduleIdRef.current;
    const isStillInValidScheduleList =
      currentScheduleId !== undefined &&
      validSchedules.some(
        scheduleRow => scheduleRow.scheduleId === currentScheduleId
      );
    if (!isStillInValidScheduleList) {
      setSelectedSchedule(validSchedules[0]);
    }
  }, [validSchedules]);

  useLayoutEffect(() => {
    const schedule = selectedScheduleRef.current;
    if (!schedule?.tabDisplayEnabled) {
      setSelectedCategoryTab(null);
      return;
    }
    const qualifiedCategoryTabList = getFirstPurchaseQualifiedCategoryTabList(
      schedule.tabs
    );
    if (qualifiedCategoryTabList.length < FIRST_PURCHASE_MIN_PAIR_COUNT) {
      setSelectedCategoryTab(null);
      return;
    }

    if (!isTabProductsSuccess || isTabProductsFetching) return;

    const mappedCategoryTabList =
      getFirstPurchaseMappedQualifiedCategoryTabList(
        qualifiedCategoryTabList,
        tabProductsMap
      );

    if (mappedCategoryTabList.length < FIRST_PURCHASE_MIN_PAIR_COUNT) {
      setSelectedCategoryTab(null);
      return;
    }

    const currentCategoryTab = selectedCategoryTabRef.current;
    const isCurrentCategoryTabsStillEligible =
      currentCategoryTab &&
      mappedCategoryTabList.some(
        tabRow => tabRow.tabId === currentCategoryTab.tabId
      );

    if (!isCurrentCategoryTabsStillEligible) {
      setSelectedCategoryTab(mappedCategoryTabList[0]);
    }
  }, [
    isTabProductsFetching,
    isTabProductsSuccess,
    selectedSchedule?.scheduleId,
    selectedSchedule?.tabDisplayEnabled,
    selectedSchedule?.tabs,
    tabProductsMap,
  ]);

  useLayoutEffect(() => {
    if (
      !isFirstPurchaseDateTabPeriod ||
      !isFirstPurchaseDateTabPrefetchResolved ||
      dateTabEligibleScheduleList.length === 0
    ) {
      return;
    }
    const currentScheduleId = selectedScheduleIdRef.current;
    const hasCurrentInEligible =
      currentScheduleId !== undefined &&
      dateTabEligibleScheduleList.some(
        scheduleRow => scheduleRow.scheduleId === currentScheduleId
      );
    if (!hasCurrentInEligible) {
      setSelectedSchedule(dateTabEligibleScheduleList[0]);
    }
  }, [
    dateTabEligibleScheduleList,
    isFirstPurchaseDateTabPeriod,
    isFirstPurchaseDateTabPrefetchResolved,
  ]);

  useLayoutEffect(() => {
    const timerId = window.setTimeout(() => {
      if (skipAutoSnapToTodayForUserDateTabRef.current) {
        return;
      }
      const schedulePoolForToday =
        isFirstPurchaseDateTabPeriod && isFirstPurchaseDateTabPrefetchResolved
          ? dateTabEligibleScheduleList
          : validSchedules;
      if (schedulePoolForToday?.length) {
        const currentSchedule = schedulePoolForToday.find(schedule =>
          isCurrentDateInRange(
            schedule.displayStartDate,
            schedule.displayEndDate
          )
        );
        if (
          currentSchedule &&
          currentSchedule.scheduleId !== selectedScheduleIdRef.current
        ) {
          setSelectedSchedule(currentSchedule);
        }
      }
    }, 200);
    return () => window.clearTimeout(timerId);
  }, [
    dateTabEligibleScheduleList,
    isCurrentDateInRange,
    isFirstPurchaseDateTabPeriod,
    isFirstPurchaseDateTabPrefetchResolved,
    validSchedules,
  ]);

  const orderedProducts = useMemo(() => {
    if (!selectedSchedule?.tabDisplayEnabled) {
      return selectedSchedule?.itemCdList
        ?.map(itemCd => tabProductsMap.get(itemCd))
        .filter((p): p is Product => !!p);
    }
    if (!selectedCategoryTab?.itemCdList?.length) return [];
    return selectedCategoryTab.itemCdList
      .map(itemCd => tabProductsMap.get(itemCd))
      .filter((p): p is Product => !!p);
  }, [tabProductsMap, selectedSchedule, selectedCategoryTab]);

  const shouldHideTabsBecauseNoProducts =
    isTabProductsSuccess &&
    !isTabProductsFetching &&
    (orderedProducts?.length ?? 0) === 0;

  const selectedScheduleIdForDateTabs =
    selectedSchedule?.scheduleId != null
      ? String(selectedSchedule.scheduleId)
      : '';

  const handleScheduleChange = useCallback(
    (schedule: PromotionFirstPurchaseSchedule, index: number) => {
      sendGa4TabClick({
        area: 'promotion_firstpurchase_tab',
        page: 'promotion',
        text: getDisplayDate(schedule.displayStartDate),
        listNo: index + 1,
        variable: title,
      });
      skipAutoSnapToTodayForUserDateTabRef.current = true;
      setMaxCount(INIT_MAX_COUNT);
      setSelectedSchedule(schedule);
      setSelectedCategoryTab(null);
    },
    [sendGa4TabClick, title]
  );

  const handleTabChange = useCallback(
    (tab: PromotionScheduleTab, index: number) => {
      const dateTabMenuVisible =
        isFirstPurchaseDateTabPeriod &&
        (dateTabEligibleScheduleList?.length ?? 0) > 0;
      sendGa4TabClick({
        area: 'promotion_firstpurchase_category_tab',
        text: tab.tabName,
        listNo: index + 1,
        variable: title,
        pageNo: dateTabMenuVisible ? getFinalPageNo() : undefined,
      });
      setMaxCount(INIT_MAX_COUNT);
      if (selectedSchedule?.tabDisplayEnabled) {
        setSelectedCategoryTab(tab);
      }
    },
    [
      sendGa4TabClick,
      dateTabEligibleScheduleList?.length,
      getFinalPageNo,
      isFirstPurchaseDateTabPeriod,
      selectedSchedule?.tabDisplayEnabled,
      title,
    ]
  );

  const tabMenuList = useMemo(() => {
    if (!isFirstPurchaseDateTabPeriod || !dateTabEligibleScheduleList?.length) {
      return [];
    }
    return dateTabEligibleScheduleList.map((scheduleRow, tabIndex) => (
      <DateTabItem
        key={scheduleRow.scheduleId}
        serverTime={getAdjustedTime()}
        startDate={scheduleRow.displayStartDate}
        endDate={scheduleRow.displayEndDate}
        value={scheduleRow.scheduleId.toString()}
        selectedValue={selectedScheduleIdForDateTabs}
        colorType={sectionColorType}
        onClick={() => handleScheduleChange(scheduleRow, tabIndex)}
      />
    ));
  }, [
    dateTabEligibleScheduleList,
    getAdjustedTime,
    handleScheduleChange,
    isFirstPurchaseDateTabPeriod,
    sectionColorType,
    selectedScheduleIdForDateTabs,
  ]);
  const categoryMenuList = useMemo(() => {
    if (!selectedSchedule?.tabDisplayEnabled) return [];
    if (!isTabProductsSuccess || isTabProductsFetching) return [];
    if (
      cmsQualifiedCategoryTabListForSelected.length <
      FIRST_PURCHASE_MIN_PAIR_COUNT
    ) {
      return [];
    }
    if (mappedQualifiedCategoryTabList.length < FIRST_PURCHASE_MIN_PAIR_COUNT) {
      return [];
    }

    const selectedCategoryTabId = selectedCategoryTab?.tabId?.toString() || '';

    return mappedQualifiedCategoryTabList.map((tab, index) =>
      isDesktop ? (
        <Menu2LineBarItem
          key={tab.tabId.toString()}
          type="type2"
          value={tab.tabId.toString()}
          text={tab.tabName}
          selectedValue={selectedCategoryTabId}
          onClick={() => handleTabChange(tab, index)}
        />
      ) : (
        <MenuBarItem
          key={tab.tabId.toString()}
          value={tab.tabId.toString()}
          text={tab.tabName}
          selectedValue={selectedCategoryTabId}
          onClick={() => handleTabChange(tab, index)}
        />
      )
    );
  }, [
    cmsQualifiedCategoryTabListForSelected.length,
    handleTabChange,
    isDesktop,
    isTabProductsFetching,
    isTabProductsSuccess,
    mappedQualifiedCategoryTabList,
    selectedCategoryTab?.tabId,
    selectedSchedule?.tabDisplayEnabled,
  ]);

  const firstPurchaseCountdownDesc = useMemo((): string | undefined => {
    if (periodType !== 'PERIOD') {
      return undefined;
    }
    const trimmed = moduleSubText?.trim();
    if (!trimmed) return undefined;
    return trimmed;
  }, [periodType, moduleSubText]);

  const handlePromotionSimpleProductClick = useCallback(
    (product: Product, index: number) => {
      sendGa4SelectItem({
        area: 'promotion_firstpurchase_item',
        text: product.itemName,
        listNo: index + 1,
        ecommercePath: '프로모션',
        product,
        tabNm: selectedCategoryTab?.tabName,
        pageNo: getFinalPageNo(),
        variable: title,
      });
    },
    [getFinalPageNo, sendGa4SelectItem, selectedCategoryTab?.tabName, title]
  );

  const expandedMaxCount = useMemo(() => {
    if (!selectedSchedule?.tabDisplayEnabled) {
      return FIRST_PURCHASE_MAX_PRODUCTS_FLAT;
    }
    return FIRST_PURCHASE_MAX_PRODUCTS_PER_TAB;
  }, [selectedSchedule?.tabDisplayEnabled]);

  const hasMoreToShow = useMemo(
    () => !!(orderedProducts?.length && orderedProducts.length > maxCount),
    [orderedProducts, maxCount]
  );

  const handlePromotionClickMore = useCallback(() => {
    if (!(orderedProducts?.length && orderedProducts.length > maxCount)) return;
    sendGa4ButtonClick({
      area: 'promotion_firstpurchase_more_button',
      text: '상품 더보기',
      page: 'promotion',
      variable: title,
    });
    setMaxCount(expandedMaxCount);
  }, [expandedMaxCount, maxCount, orderedProducts, sendGa4ButtonClick, title]);

  const couponCdTrimmed = selectedSchedule?.couponCd?.trim() ?? '';

  const couponDownloadClickHandler = useMemo(():
    | (() => Promise<void>)
    | undefined => {
    if (!couponCdTrimmed) return undefined;
    const dateTabMenuVisible =
      isFirstPurchaseDateTabPeriod &&
      (dateTabEligibleScheduleList?.length ?? 0) > 0;
    return async () => {
      const couponNameForGa =
        selectedSchedule?.couponItem?.couponName ??
        DEFAULT_FIRST_PURCHASE_COUPON_TITLE;
      sendGa4CouponClick({
        area: 'promotion_firstpurchase_coupon_download',
        couponCd: couponCdTrimmed,
        couponNm: couponNameForGa,
        text: couponNameForGa,
        pageNo: dateTabMenuVisible ? getFinalPageNo() : undefined,
      });
      await downloadSingleCoupon(couponCdTrimmed);
    };
  }, [
    couponCdTrimmed,
    dateTabEligibleScheduleList?.length,
    downloadSingleCoupon,
    getFinalPageNo,
    isFirstPurchaseDateTabPeriod,
    selectedSchedule?.couponItem?.couponName,
    sendGa4CouponClick,
  ]);

  const productListForSection = useMemo((): ReactNode[] => {
    if (
      !isDesktop &&
      (!isTabProductsSuccess || orderedProducts?.length === 0)
    ) {
      return productSkeletonKeyList.map(skeletonKey => (
        <PrdItemSkeleton
          key={skeletonKey}
          bgType={promotionProductSkeletonBgType}
        />
      ));
    }

    const productListLength = orderedProducts?.length ?? 0;
    if (productListLength === 0) {
      return [];
    }

    return renderProducts({
      type: 'simple',
      maxCount,
      rs: isDesktop ? '417' : '216',
      products: orderedProducts as Product[],
      isBrandNameKr: true,
      colorType: promotionProductUnitColorType,
      onClickProduct: handlePromotionSimpleProductClick,
    });
  }, [
    handlePromotionSimpleProductClick,
    isDesktop,
    isTabProductsSuccess,
    maxCount,
    orderedProducts,
    productSkeletonKeyList,
    promotionProductSkeletonBgType,
    promotionProductUnitColorType,
    renderProducts,
  ]);

  if (validSchedules.length === 0 || !selectedSchedule) return null;

  if (
    periodType === 'ALL' &&
    selectedSchedule.tabDisplayEnabled &&
    getFirstPurchaseQualifiedCategoryTabList(selectedSchedule.tabs).length <
      FIRST_PURCHASE_MIN_PAIR_COUNT
  ) {
    return null;
  }

  if (
    selectedSchedule.tabDisplayEnabled &&
    isTabProductsSuccess &&
    !isTabProductsFetching &&
    cmsQualifiedCategoryTabListForSelected.length >=
      FIRST_PURCHASE_MIN_PAIR_COUNT &&
    mappedQualifiedCategoryTabList.length < FIRST_PURCHASE_MIN_PAIR_COUNT
  ) {
    return null;
  }

  if (
    isFirstPurchaseDateTabPeriod &&
    validSchedules.length < FIRST_PURCHASE_MIN_PAIR_COUNT
  ) {
    return null;
  }

  if (
    firstPurchaseDateTabPrefetchEnabled &&
    !isFirstPurchaseDateTabPrefetchResolved
  ) {
    return null;
  }

  if (
    isFirstPurchaseDateTabPeriod &&
    dateTabEligibleScheduleList.length < FIRST_PURCHASE_MIN_PAIR_COUNT
  ) {
    return null;
  }

  if (shouldHideTabsBecauseNoProducts) return null;

  const bannerImgSrc = isDesktop
    ? titleBannerPcImageUrl
    : titleBannerMoImageUrl;
  const activeCouponCd = selectedSchedule?.couponCd?.trim();
  const enrichedCouponItem = selectedSchedule?.couponItem;
  const useEnrichedCouponCopy = Boolean(activeCouponCd && enrichedCouponItem);

  let couponTitleProp: string | undefined;
  let couponDescProp: string | undefined;
  if (activeCouponCd) {
    if (useEnrichedCouponCopy && enrichedCouponItem) {
      couponTitleProp = enrichedCouponItem.couponName;
      const couponDescText = getCouponDescFromInfo(enrichedCouponItem);
      couponDescProp = couponDescText || undefined;
    } else {
      couponTitleProp = DEFAULT_FIRST_PURCHASE_COUPON_TITLE;
      couponDescProp = moduleSubText?.trim() || undefined;
    }
  }

  return (
    <SpecialPriceProductSection
      colorType={sectionColorType}
      imgSrc={bannerImgSrc}
      title={title}
      subTitle={subTitle}
      startDate={selectedSchedule?.displayStartDate}
      endDate={selectedSchedule?.displayEndDate}
      tabMenuList={tabMenuList}
      selDateTab={selectedScheduleIdForDateTabs}
      selectedTabId={selectedScheduleIdForDateTabs}
      categoryTabType="type1"
      categoryTabList={categoryMenuList}
      selectedCateTabId={selectedCategoryTab?.tabId?.toString() || ''}
      couponTitle={couponTitleProp}
      couponDesc={couponDescProp}
      onClickCoupon={couponDownloadClickHandler}
      countdownDesc={firstPurchaseCountdownDesc}
      productList={productListForSection}
      serverTime={getAdjustedTime()}
      isDesktop={isDesktop}
      isMore={!hasMoreToShow}
      isLink={false}
      onClickMore={handlePromotionClickMore}
    />
  );
}

function FirstPurchaseModule({
  bgType,
  visualTokens,
  isDesktop,
  subTitle,
  title,
  promotionId,
  periodType,
  schedules,
  contentId,
  promotionTitle,
  isVisible,
  titleBannerPcImageUrl,
  titleBannerMoImageUrl,
  moduleSubText,
}: FirstPurchaseModuleProps) {
  const { assistData } = useGlobalState();
  if (!shouldShowFirstPurchaseModule(assistData.isLogin, assistData.user)) {
    return null;
  }
  return (
    <FirstPurchaseModuleContent
      bgType={bgType}
      visualTokens={visualTokens}
      isDesktop={isDesktop}
      subTitle={subTitle}
      title={title}
      promotionId={promotionId}
      promotionTitle={promotionTitle}
      periodType={periodType}
      schedules={schedules}
      contentId={contentId}
      isVisible={isVisible}
      titleBannerPcImageUrl={titleBannerPcImageUrl}
      titleBannerMoImageUrl={titleBannerMoImageUrl}
      moduleSubText={moduleSubText}
    />
  );
}

export default memo(FirstPurchaseModule);
